'use client';

import { useState } from 'react';
import { Mail, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

interface Props {
  variant?: 'inline' | 'card' | 'hero';
  title?: string;
  description?: string;
  placeholder?: string;
  buttonText?: string;
  showNameField?: boolean;
}

export function NewsletterSubscribe({
  variant = 'inline',
  title = 'Subscribe to Our Newsletter',
  description = 'Get the latest EV industry insights, technical guides, and exclusive offers delivered to your inbox.',
  placeholder = 'Enter your email',
  buttonText = 'Subscribe',
  showNameField = false,
}: Props) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    setMessage('');

    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: firstName }),
      });
      const data = await res.json();

      if (data.success) {
        setStatus('success');
        setMessage(data.message || "You're subscribed!");
        setEmail('');
        setFirstName('');
      } else {
        setStatus('error');
        setMessage(data.error || 'Something went wrong.');
      }
    } catch {
      setStatus('error');
      setMessage('Failed to subscribe. Please try again.');
    }
  };

  if (variant === 'hero') {
    return (
      <div className="relative py-16 md:py-20 px-6 md:px-10 rounded-3xl overflow-hidden bg-gradient-to-br from-[var(--color-surface)] via-[var(--color-surface-light)] to-[var(--color-surface)] border border-[var(--color-primary)]/20">
        {/* Glow */}
        <div
          className="absolute top-0 right-0 w-96 h-96 opacity-30 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, var(--color-glow-soft), transparent 60%)',
          }}
        />

        <div className="relative max-w-3xl mx-auto text-center">
          <Mail className="w-12 h-12 mx-auto mb-6 text-[var(--color-primary)]" />
          <h3 className="text-2xl md:text-3xl font-bold mb-4">{title}</h3>
          <p className="text-[var(--color-muted)] mb-8 max-w-xl mx-auto">{description}</p>

          {status === 'success' ? (
            <div className="flex items-center justify-center gap-3 py-6 px-8 rounded-2xl bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/30">
              <CheckCircle className="w-8 h-8 text-[var(--color-primary)] flex-shrink-0" />
              <p className="text-lg font-medium text-[var(--color-foreground)]">{message}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                {showNameField && (
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Your name"
                    className="flex-1 px-5 py-4 rounded-xl bg-[var(--color-background)]/50 border border-[var(--color-border)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted)] focus:outline-none focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] transition-all"
                  />
                )}
                <div className="flex-1 relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-muted)]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={placeholder}
                    required
                    className="w-full pl-12 pr-4 py-4 rounded-xl bg-[var(--color-background)]/50 border border-[var(--color-border)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted)] focus:outline-none focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] transition-all"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-8 py-4 rounded-xl font-semibold text-[var(--color-background)] bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] hover:shadow-lg hover:shadow-[var(--color-glow-soft)] hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Subscribing...
                    </>
                  ) : (
                    <>
                      {buttonText}
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
              {status === 'error' && (
                <p className="mt-3 text-sm text-red-400 flex items-center gap-1.5 justify-center">
                  <AlertCircle className="w-4 h-4" />
                  {message}
                </p>
              )}
              <p className="mt-4 text-xs text-[var(--color-muted)]">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </form>
          )}
        </div>
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div className="p-6 md:p-8 rounded-2xl bg-[var(--color-surface)] border border-[var(--color-border)]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[var(--color-primary)]/10 flex items-center justify-center">
            <Mail className="w-5 h-5 text-[var(--color-primary)]" />
          </div>
          <h4 className="font-semibold">{title}</h4>
        </div>
        <p className="text-sm text-[var(--color-muted)] mb-5">{description}</p>

        {status === 'success' ? (
          <div className="flex items-center gap-2 py-3 px-4 rounded-lg bg-[var(--color-primary)]/10 text-sm text-[var(--color-primary)]">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            {message}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-muted)]" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={placeholder}
                required
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-[var(--color-background)] border border-[var(--color-border)] text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted)] focus:outline-none focus:border-[var(--color-primary)] transition-all"
              />
            </div>
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full py-2.5 rounded-lg text-sm font-semibold text-[var(--color-background)] bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {status === 'loading' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                buttonText
              )}
            </button>
            {status === 'error' && (
              <p className="text-xs text-red-400 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {message}
              </p>
            )}
          </form>
        )}
      </div>
    );
  }

  // inline variant
  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <div className="flex-1 relative">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          required
          className="w-full px-4 py-2.5 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted)] focus:outline-none focus:border-[var(--color-primary)] transition-all"
        />
      </div>
      <button
        type="submit"
        disabled={status === 'loading'}
        className="px-4 py-2.5 rounded-lg text-sm font-medium text-[var(--color-background)] bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] transition-colors disabled:opacity-70"
      >
        {status === 'loading' ? '...' : buttonText}
      </button>
    </form>
  );
}
